import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, User, Mail, Phone, Building, Shield, FileText, CreditCard, Truck, Package, CheckCircle, AlertCircle, Settings, Users } from 'lucide-react';
import { toast } from 'react-hot-toast';

const ContactManagement = ({ customerId, contacts = [], onContactsChange }) => {
  const [isAddingContact, setIsAddingContact] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [contactForm, setContactForm] = useState({
    firstName: '',
    lastName: '',
    title: '',
    department: '',
    email: '',
    phone: '',
    mobile: '',
    fax: '',
    isPrimary: false,
    roles: [],
    notes: ''
  });

  const [roleForm, setRoleForm] = useState({
    role: 'PRIMARY',
    isDefault: false,
    notes: ''
  });

  // Must match the backend ContactRole enum — values not in the enum are
  // silently coerced to OTHER on save, losing the user's choice.
  const contactRoles = [
    { value: 'PRIMARY', label: 'Primary Contact', icon: User, color: 'bg-blue-100 text-blue-800' },
    { value: 'BILLING', label: 'Billing', icon: CreditCard, color: 'bg-green-100 text-green-800' },
    { value: 'SHIPPING', label: 'Shipping', icon: Truck, color: 'bg-yellow-100 text-yellow-800' },
    { value: 'TECHNICAL', label: 'Technical Support', icon: Settings, color: 'bg-gray-100 text-gray-800' },
    { value: 'SALES', label: 'Sales', icon: Users, color: 'bg-orange-100 text-orange-800' },
    { value: 'ACCOUNT_MANAGER', label: 'Account Manager', icon: Building, color: 'bg-purple-100 text-purple-800' },
    { value: 'ADMINISTRATIVE', label: 'Administrative', icon: FileText, color: 'bg-indigo-100 text-indigo-800' },
    { value: 'OTHER', label: 'Other', icon: User, color: 'bg-gray-100 text-gray-800' }
  ];

  const resetForm = () => {
    setContactForm({
      firstName: '',
      lastName: '',
      title: '',
      department: '',
      email: '',
      phone: '',
      mobile: '',
      fax: '',
      isPrimary: false,
      roles: [],
      notes: ''
    });
    setRoleForm({
      role: 'PRIMARY',
      isDefault: false,
      notes: ''
    });
  };

  const handleAddContact = () => {
    if (!contactForm.firstName || !contactForm.lastName) {
      toast.error('First name and last name are required');
      return;
    }

    if (contactForm.roles.length === 0) {
      toast.error('At least one role must be selected');
      return;
    }

    const newContact = {
      id: `temp-${Date.now()}`,
      customerId,
      ...contactForm,
      isActive: true,
      createdAt: new Date(),
      updatedAt: new Date(),
      contactRoles: contactForm.roles.map(role => ({
        id: `temp-role-${Date.now()}-${role}`,
        role,
        isDefault: role === 'PRIMARY',
        isActive: true,
        notes: roleForm.notes
      }))
    };

    onContactsChange([...contacts, newContact]);
    resetForm();
    setIsAddingContact(false);
    toast.success('Contact added successfully');
  };

  const handleEditContact = (contact) => {
    setEditingContact(contact);
    setContactForm({
      firstName: contact.firstName,
      lastName: contact.lastName,
      title: contact.title || '',
      department: contact.department || '',
      email: contact.email || '',
      phone: contact.phone || '',
      mobile: contact.mobile || '',
      fax: contact.fax || '',
      isPrimary: contact.isPrimary,
      roles: contact.contactRoles?.map(cr => cr.role) || [],
      notes: contact.notes || ''
    });
  };

  const handleUpdateContact = () => {
    if (!contactForm.firstName || !contactForm.lastName) {
      toast.error('First name and last name are required');
      return;
    }

    if (contactForm.roles.length === 0) {
      toast.error('At least one role must be selected');
      return;
    }

    const updatedContacts = contacts.map(contact => {
      if (contact.id === editingContact.id) {
        return {
          ...contact,
          ...contactForm,
          updatedAt: new Date(),
          contactRoles: contactForm.roles.map(role => ({
            id: `temp-role-${Date.now()}-${role}`,
            role,
            isDefault: role === 'PRIMARY',
            isActive: true,
            notes: roleForm.notes
          }))
        };
      }
      return contact;
    });

    onContactsChange(updatedContacts);
    setEditingContact(null);
    resetForm();
    toast.success('Contact updated successfully');
  };

  const handleDeleteContact = (contactId) => {
    if (window.confirm('Are you sure you want to delete this contact?')) {
      const updatedContacts = contacts.filter(contact => contact.id !== contactId);
      onContactsChange(updatedContacts);
      toast.success('Contact deleted successfully');
    }
  };

  const handleRoleToggle = (role) => {
    setContactForm(prev => ({
      ...prev,
      roles: prev.roles.includes(role)
        ? prev.roles.filter(r => r !== role)
        : [...prev.roles, role]
    }));
  };

  const getRoleIcon = (role) => {
    const roleInfo = contactRoles.find(r => r.value === role);
    return roleInfo ? roleInfo.icon : User;
  };

  const getRoleColor = (role) => {
    const roleInfo = contactRoles.find(r => r.value === role);
    return roleInfo ? roleInfo.color : 'bg-gray-100 text-gray-800';
  };

  const getRoleLabel = (role) => {
    const roleInfo = contactRoles.find(r => r.value === role);
    return roleInfo ? roleInfo.label : role;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
          <Users size={20} />
          Contact Management
        </h3>
        <button type="button"          onClick={() => setIsAddingContact(true)}
          className="bg-primary-600 text-white px-3 py-2 rounded-lg hover:bg-primary-700 flex items-center gap-2"
          title="Add contact"
        >
          <Plus size={16} />
          Add Contact
        </button>
      </div>

      {/* Add/Edit Contact Form */}
      {(isAddingContact || editingContact) && (
        <div className="bg-white p-6 rounded-lg shadow border">
          <h4 className="text-md font-medium text-gray-900 mb-4">
            {editingContact ? 'Edit Contact' : 'Add New Contact'}
          </h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
              <input
                type="text"
                value={contactForm.firstName}
                onChange={(e) => setContactForm(prev => ({ ...prev, firstName: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
              <input
                type="text"
                value={contactForm.lastName}
                onChange={(e) => setContactForm(prev => ({ ...prev, lastName: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
              <input
                type="text"
                value={contactForm.title}
                onChange={(e) => setContactForm(prev => ({ ...prev, title: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
              <input
                type="text"
                value={contactForm.department}
                onChange={(e) => setContactForm(prev => ({ ...prev, department: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={contactForm.email}
                onChange={(e) => setContactForm(prev => ({ ...prev, email: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input
                type="tel"
                value={contactForm.phone}
                onChange={(e) => setContactForm(prev => ({ ...prev, phone: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Mobile</label>
              <input
                type="tel"
                value={contactForm.mobile}
                onChange={(e) => setContactForm(prev => ({ ...prev, mobile: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fax</label>
              <input
                type="tel"
                value={contactForm.fax}
                onChange={(e) => setContactForm(prev => ({ ...prev, fax: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          {/* Roles Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Roles *</label>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {contactRoles.map((roleInfo) => {
                const Icon = roleInfo.icon;
                return (
                  <label key={roleInfo.value} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={contactForm.roles.includes(roleInfo.value)}
                      onChange={() => handleRoleToggle(roleInfo.value)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700">{roleInfo.label}</span>
                  </label>
                );
              })}
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={contactForm.notes}
              onChange={(e) => setContactForm(prev => ({ ...prev, notes: e.target.value }))}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div className="flex justify-end gap-3">
            <button type="button"              onClick={() => {
                setIsAddingContact(false);
                setEditingContact(null);
                resetForm();
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              title="Cancel"
            >
              Cancel
            </button>
            <button type="button"              onClick={editingContact ? handleUpdateContact : handleAddContact}
              className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700"
              title="Save contact"
            >
              {editingContact ? 'Update Contact' : 'Add Contact'}
            </button>
          </div>
        </div>
      )}

      {/* Contacts List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Roles
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact Info
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {contacts.map((contact) => (
                <tr key={contact.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {contact.firstName} {contact.lastName}
                      </div>
                      {contact.title && (
                        <div className="text-sm text-gray-500">{contact.title}</div>
                      )}
                      {contact.department && (
                        <div className="text-xs text-gray-400">{contact.department}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-wrap gap-1">
                      {contact.contactRoles?.map((role) => {
                        const Icon = getRoleIcon(role.role);
                        return (
                          <span
                            key={role.id}
                            className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${getRoleColor(role.role)}`}
                          >
                            <Icon size={12} />
                            {getRoleLabel(role.role)}
                          </span>
                        );
                      })}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="space-y-1">
                      {contact.email && (
                        <div className="flex items-center gap-1 text-sm text-gray-600">
                          <Mail size={14} />
                          {contact.email}
                        </div>
                      )}
                      {contact.phone && (
                        <div className="flex items-center gap-1 text-sm text-gray-600">
                          <Phone size={14} />
                          {contact.phone}
                        </div>
                      )}
                      {contact.mobile && (
                        <div className="flex items-center gap-1 text-sm text-gray-600">
                          <Phone size={14} />
                          {contact.mobile} (Mobile)
                        </div>
                      )}
                      {contact.fax && (
                        <div className="flex items-center gap-1 text-sm text-gray-600">
                          <Phone size={14} />
                          {contact.fax} (Fax)
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        contact.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {contact.isActive ? 'Active' : 'Inactive'}
                      </span>
                      {contact.isPrimary && (
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                          Primary
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center gap-2">
                      <button type="button"                        onClick={() => handleEditContact(contact)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Edit Contact"
                      >
                        <Edit size={16} />
                      </button>
                      <button type="button"                        onClick={() => handleDeleteContact(contact.id)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete Contact"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {contacts.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <Users size={48} className="mx-auto mb-4 text-gray-300" />
            <p className="text-lg font-medium text-gray-900 mb-2">No contacts found</p>
            <p className="text-gray-500">Add your first contact to get started.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContactManagement;
